/**
 * The classroom, from both sides.
 *
 * The privacy model is the whole design, and it is the server's model — this
 * screen does not invent one. Handwriting is private. An instructor sees a
 * document only through a `ShareGrant` the student created, for one document and
 * one class, and can revoke nothing on the student's behalf. Everything here is
 * a thin, honest surface over endpoints that already enforce that.
 *
 * What is deliberately absent, because the brief forbids it and because it would
 * change what the app is for: rankings, comparisons between students, streak
 * pressure, last-seen timestamps presented as attendance, and any route by which
 * an instructor reaches work that was not handed to them.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from '../design/Button';
import { TextField } from '../design/Field';
import { Alert, Card, Dialog } from '../design/Feedback';
import { FailureSummary, useSubmission } from '../auth/formSupport';
import { useAuth } from '../auth/AuthProvider';
import { repositories } from '../storage/repositories';
import type { StoredDocument } from '../storage/types';

interface ClassRoom {
  id: string;
  name: string;
  role: 'student' | 'instructor';
  signup_open: boolean;
  member_count: number;
}

interface MemberSummary {
  user_id: string;
  display_name: string;
  joined_at: string;
  current_unit: string | null;
  mastery_percent: number;
  due_reviews: number;
  last_practised_at: string | null;
}

interface ShareGrant {
  id: string;
  document_id: string;
  class_id: string;
  created_at: string;
  revoked_at: string | null;
}

export function Classroom() {
  const { api, state } = useAuth();
  const [classes, setClasses] = useState<ClassRoom[] | null>(null);
  const { busy, failure, submit } = useSubmission();
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setClasses(await api.get<ClassRoom[]>('/classes'));
  }, [api]);

  useEffect(() => {
    if (state.status !== 'signed-in') return;
    void load().catch(() => setClasses([]));
  }, [state.status, load]);

  if (state.status !== 'signed-in') {
    return (
      <div className="feature classroom">
        <header className="feature-head">
          <div>
            <p className="eyebrow">Class</p>
            <h2>Classes need an account.</h2>
            <p className="lede">
              Everything else in MusicMentor works signed out and stays on this iPad. A class is
              the one thing that needs an account, because it involves somebody else.
            </p>
          </div>
        </header>
      </div>
    );
  }

  const student = classes?.filter((room) => room.role === 'student') ?? [];
  const teaching = classes?.filter((room) => room.role === 'instructor') ?? [];

  return (
    <div className="feature classroom">
      <header className="feature-head">
        <div>
          <p className="eyebrow">Class</p>
          <h2>Your class, and what you choose to show.</h2>
          <p className="lede">
            Your writing is private. A teacher sees a piece of work only when you hand that piece
            of work to that class — never your whole iPad, and never anything you have not shared.
          </p>
        </div>
      </header>

      {notice ? <Alert tone="success">{notice}</Alert> : null}
      <FailureSummary failure={failure} />

      <JoinClass
        onJoined={async (name) => {
          setNotice(`You have joined ${name}.`);
          await load();
        }}
      />

      {student.map((room) => (
        <StudentClassCard
          key={room.id}
          room={room}
          onLeft={async () => {
            setNotice(`You have left ${room.name}.`);
            await load();
          }}
          onNotice={setNotice}
        />
      ))}

      <CreateClass onCreated={load} busy={busy} submit={submit} />

      {teaching.map((room) => (
        <InstructorClassCard key={room.id} room={room} onNotice={setNotice} onChanged={load} />
      ))}

      {classes !== null && classes.length === 0 ? (
        <p className="classroom__empty">
          You are not in a class yet. Ask your teacher for a join code.
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- student

function JoinClass({ onJoined }: { onJoined: (name: string) => Promise<void> }) {
  const { api } = useAuth();
  const [code, setCode] = useState('');
  const { busy, failure, submit } = useSubmission();

  return (
    <Card title="Join a class">
      <FailureSummary failure={failure} handledFields={['join_code']} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit(async () => {
            const room = await api.post<ClassRoom>('/classes/join', { join_code: code.trim() });
            setCode('');
            await onJoined(room.name);
          });
        }}
        noValidate
      >
        <TextField
          label="Join code"
          value={code}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          required
          help="From your teacher. It only adds you to the class — it is not a password."
          error={failure?.fields.join_code}
          onChange={(event) => setCode(event.target.value)}
        />
        <div className="mm-auth__actions">
          <Button type="submit" variant="primary" loading={busy}>
            Join
          </Button>
        </div>
      </form>
    </Card>
  );
}

function StudentClassCard({
  room,
  onLeft,
  onNotice,
}: {
  room: ClassRoom;
  onLeft: () => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const { api } = useAuth();
  const [documents, setDocuments] = useState<StoredDocument[]>([]);
  const [shares, setShares] = useState<ShareGrant[]>([]);
  const [leaving, setLeaving] = useState(false);
  const { busy, failure, submit } = useSubmission();

  useEffect(() => {
    void repositories()
      .documents.list()
      .then((all) => setDocuments(all.filter((document) => document.payload.strokes.length > 0)));
  }, []);

  const sharedIds = new Set(shares.filter((grant) => !grant.revoked_at).map((g) => g.document_id));

  const share = (document: StoredDocument) =>
    void submit(async () => {
      const grant = await api.post<ShareGrant>('/classes/share', {
        document_id: document.id,
        class_id: room.id,
      });
      setShares((current) => [...current, grant]);
      onNotice(`“${document.title || 'Untitled'}” is now visible to ${room.name}.`);
    });

  const unshare = (document: StoredDocument) =>
    void submit(async () => {
      const grant = shares.find((g) => g.document_id === document.id && !g.revoked_at);
      if (!grant) return;
      await api.delete(`/classes/share/${grant.id}`);
      setShares((current) => current.filter((g) => g.id !== grant.id));
      onNotice(`“${document.title || 'Untitled'}” is private again.`);
    });

  return (
    <Card title={room.name}>
      <FailureSummary failure={failure} />
      <p className="classroom__note">
        You are a student in this class. {room.member_count} member
        {room.member_count === 1 ? '' : 's'}.
      </p>

      <h4 className="classroom__subhead">Share a piece of work</h4>
      <p className="classroom__note">
        Sharing hands over one document. It does not give your teacher your other work, your
        account, or anything you write later.
      </p>

      {documents.length === 0 ? (
        <p className="classroom__empty">Nothing written yet — there is nothing to share.</p>
      ) : (
        <ul className="classroom__documents">
          {documents.map((document) => {
            const isShared = sharedIds.has(document.id);
            return (
              <li key={document.id} className="classroom__document">
                <span className="classroom__document-name">{document.title || 'Untitled'}</span>
                <span className="classroom__document-state">
                  {isShared ? 'Shared with this class' : 'Private'}
                </span>
                <Button
                  variant={isShared ? 'tertiary' : 'secondary'}
                  disabled={busy}
                  onClick={() => (isShared ? unshare(document) : share(document))}
                >
                  {isShared ? 'Stop sharing' : 'Share with class'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mm-auth__actions">
        <Button variant="tertiary" onClick={() => setLeaving(true)}>
          Leave this class
        </Button>
      </div>

      <Dialog
        open={leaving}
        title={`Leave ${room.name}?`}
        confirmLabel="Leave the class"
        cancelLabel="Stay"
        onCancel={() => setLeaving(false)}
        onConfirm={() => {
          setLeaving(false);
          void submit(async () => {
            await api.delete(`/classes/${room.id}/leave`);
            await onLeft();
          });
        }}
      >
        <p>
          Your work stays yours — leaving removes you from the class and nothing else. Anything you
          shared stops being visible to the teacher.
        </p>
      </Dialog>
    </Card>
  );
}

// ------------------------------------------------------------- instructor

function CreateClass({
  onCreated,
  busy,
  submit,
}: {
  onCreated: () => Promise<void>;
  busy: boolean;
  submit: (work: () => Promise<void>) => Promise<boolean>;
}) {
  const { api } = useAuth();
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);

  if (!open) {
    return (
      <div className="mm-auth__aside">
        <button type="button" className="mm-auth__link" onClick={() => setOpen(true)}>
          I am a teacher — create a class
        </button>
      </div>
    );
  }

  return (
    <Card title="Create a class">
      {code ? (
        <>
          <Alert tone="success">
            Class created. This join code is shown once — write it down now.
          </Alert>
          {/* Shown exactly once because the server stores only a slow hash of it.
              There is no way to display it again, and pretending otherwise would
              be a lie the learner discovers at the worst moment. */}
          <p className="classroom__code">{code}</p>
          <Button variant="secondary" onClick={() => { setCode(null); setOpen(false); }}>
            Done
          </Button>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit(async () => {
              const created = await api.post<{ id: string; join_code?: string }>('/classes', {
                name: name.trim(),
              });
              setCode(created.join_code ?? null);
              setName('');
              await onCreated();
            });
          }}
          noValidate
        >
          <TextField
            label="Class name"
            value={name}
            required
            onChange={(event) => setName(event.target.value)}
          />
          <div className="mm-auth__actions">
            <Button type="submit" variant="primary" loading={busy}>
              Create class
            </Button>
            <Button variant="tertiary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function InstructorClassCard({
  room,
  onNotice,
  onChanged,
}: {
  room: ClassRoom;
  onNotice: (message: string) => void;
  onChanged: () => Promise<void>;
}) {
  const { api } = useAuth();
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [shared, setShared] = useState<ShareGrant[]>([]);
  const [rotated, setRotated] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MemberSummary | null>(null);
  const { busy, failure, submit } = useSubmission();

  const load = useCallback(async () => {
    setMembers(await api.get<MemberSummary[]>(`/instructor/${room.id}/members`));
    setShared(await api.get<ShareGrant[]>(`/instructor/${room.id}/shared`));
  }, [api, room.id]);

  useEffect(() => {
    void load().catch(() => setMembers([]));
  }, [load]);

  return (
    <Card title={`${room.name} — you teach this`}>
      <FailureSummary failure={failure} />

      <div className="mm-auth__actions">
        <Button
          variant="secondary"
          loading={busy}
          onClick={() =>
            void submit(async () => {
              const next = await api.post<{ join_code: string }>(
                `/classes/${room.id}/rotate-code`,
              );
              setRotated(next.join_code);
            })
          }
        >
          New join code
        </Button>
        <a
          className="mm-auth__link"
          href={`/api/v1/instructor/${room.id}/members.csv`}
          download={`${room.name}-progress.csv`}
        >
          Download progress CSV
        </a>
      </div>

      {rotated ? (
        <>
          <Alert tone="warning">
            The old code no longer works. This one is shown once.
          </Alert>
          <p className="classroom__code">{rotated}</p>
        </>
      ) : null}

      <h4 className="classroom__subhead">Members</h4>
      {members === null ? (
        <p className="classroom__empty">Loading…</p>
      ) : members.length === 0 ? (
        <p className="classroom__empty">Nobody has joined yet.</p>
      ) : (
        <table className="classroom__members">
          <caption className="mm-visually-hidden">
            Members of {room.name}, with their current unit and review load
          </caption>
          <thead>
            <tr>
              <th scope="col">Student</th>
              <th scope="col">Unit</th>
              <th scope="col">Mastery</th>
              <th scope="col">Due</th>
              <th scope="col"><span className="mm-visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.user_id}>
                <th scope="row">{member.display_name || 'Unnamed'}</th>
                <td>{member.current_unit ?? '—'}</td>
                {/* Descriptive, not competitive: no ranking, no comparison, no
                    ordering by score. */}
                <td>{member.mastery_percent}%</td>
                <td>{member.due_reviews}</td>
                <td>
                  <Button variant="tertiary" onClick={() => setRemoving(member)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 className="classroom__subhead">Work shared with you</h4>
      {shared.length === 0 ? (
        <p className="classroom__empty">
          Nothing has been shared. Students choose what you can see, one piece at a time.
        </p>
      ) : (
        <ul className="classroom__documents">
          {shared.map((grant) => (
            <li key={grant.id} className="classroom__document">
              <span className="classroom__document-name">{grant.document_id.slice(0, 8)}</span>
              <span className="classroom__document-state">
                shared {new Date(grant.created_at).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={removing !== null}
        title={removing ? `Remove ${removing.display_name || 'this student'}?` : ''}
        destructive
        confirmLabel="Remove from class"
        cancelLabel="Keep them"
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const member = removing;
          setRemoving(null);
          if (!member) return;
          void submit(async () => {
            await api.delete(`/instructor/${room.id}/members/${member.user_id}`);
            onNotice(`${member.display_name || 'That student'} was removed from ${room.name}.`);
            await load();
            await onChanged();
          });
        }}
      >
        <p>
          This removes them from the class only. Their account, their writing and their progress
          are untouched and stay theirs.
        </p>
      </Dialog>
    </Card>
  );
}
